import { API_URL } from '@/consts/urls';
import { getContext } from '@/context';

export const DEFAULT_AVATAR =
  'https://cdn190.picsart.com/232804661007900.png?to=fixed&r=100x100';

export const fetchUserAvatar = async (userId: number): Promise<string> => {
  try {
    const response = await getContext().utils.api.fetch(
      `${API_URL}/users/${userId}/profile`
    );
    if (!response.ok) return DEFAULT_AVATAR;
    const json = await response.json();
    const photo = json?.response
      ?.flatMap(
        (card: { data?: { user?: { photo?: string } }[] }) => card.data ?? []
      )
      .find((item: { user?: { photo?: string } }) => item?.user?.photo)
      ?.user?.photo;
    return photo || DEFAULT_AVATAR;
  } catch {
    return DEFAULT_AVATAR;
  }
};

// Social contact handling, ported from user-profile (SocialLinks + getContactUrl).
const SOCIAL_LABEL: Record<string, string> = {
  email: 'Email',
  instagram: 'Instagram',
  website: 'Website',
  site: 'Website',
  pinterest: 'Pinterest',
};

const getContactUrl = (type: string, value: string): string => {
  switch (type) {
    case 'instagram':
      return `https://instagram.com/${value.replace('@', '')}`;
    case 'pinterest':
      return `https://pinterest.com/${value.replace('@', '')}`;
    case 'email':
      return `mailto:${value}`;
    case 'website':
    case 'site':
      return value.startsWith('http') ? value : `https://${value}`;
    default:
      return value;
  }
};

export interface SocialLink {
  label: string;
  url: string;
}

export interface CurrentUserProfile {
  name?: string;
  username?: string;
  /** photos + stickers + videos, matching user-profile's postsCount. */
  postsCount: number;
  socials: SocialLink[];
}

interface ApiContact {
  value?: string;
  type?: string;
}

interface ApiCurrentUser {
  name?: string;
  username?: string;
  photos_count?: number;
  stickers_count?: number;
  video_count?: number;
  brand?: { contacts?: ApiContact[] };
}

/**
 * Current user's public profile from /users/show/me.json — the same source
 * user-profile uses. postsCount = photos + stickers + videos; socials come from
 * brand.contacts (labelled + URL-built like user-profile's SocialLinks).
 */
export const fetchCurrentUserProfile =
  async (): Promise<CurrentUserProfile> => {
    try {
      const response = await getContext().utils.api.fetch(
        `${API_URL}/users/show/me.json`
      );
      if (!response.ok) return { postsCount: 0, socials: [] };

      const user: ApiCurrentUser = await response.json();

      const postsCount =
        Number(user.photos_count ?? 0) +
        Number(user.stickers_count ?? 0) +
        Number(user.video_count ?? 0);

      const socials: SocialLink[] = (user.brand?.contacts ?? [])
        .filter((c) => c?.type && c?.value && SOCIAL_LABEL[c.type])
        .map((c) => ({
          label: SOCIAL_LABEL[c.type as string],
          url: getContactUrl(c.type as string, c.value as string),
        }));

      return {
        name: user.name || undefined,
        username: user.username || undefined,
        postsCount,
        socials,
      };
    } catch {
      return { postsCount: 0, socials: [] };
    }
  };
